import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Card, CardHeader, Button, Badge, Input, Select, Spinner } from '@tkpod/ui';
import { api } from '@/lib/api';
import { useDebounce } from '@/hooks/use-debounce';
import { Pagination } from '@/components/Pagination';

type DesignContextFilter = 'ALL' | 'STOREFRONT' | 'PRODUCT';
type StatusFilter = 'ALL' | 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
type TypeFilter = 'ALL' | 'DTF_BY_SIZE' | 'GANGSHEET' | 'UV_STICKER' | 'APPAREL' | 'DRINKWARE' | 'ACCESSORIES' | 'HOME_DECOR' | 'STATIONERY' | 'CUSTOM';

interface Category {
  id: string;
  name: string;
}

interface Template {
  id: string;
  name: string;
  slug: string;
  productType: string;
  status: string;
  designerMode: string;
  categoryName: string | null;
  thumbnailUrl: string | null;
  brand: string | null;
  modelName: string | null;
  presetCount: number;
  optionSummary: string | null;
  variantCount: number;
  createdAt: string;
}

interface TemplatesResponse {
  data: Template[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

const CONTEXT_OPTIONS: { value: DesignContextFilter; label: string }[] = [
  { value: 'ALL', label: 'All Contexts' },
  { value: 'STOREFRONT', label: 'Storefront' },
  { value: 'PRODUCT', label: 'Product' },
];

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const TYPE_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'ALL', label: 'All Types' },
  { value: 'DTF_BY_SIZE', label: 'DTF by Size' },
  { value: 'GANGSHEET', label: 'Gang Sheet' },
  { value: 'UV_STICKER', label: 'UV Sticker' },
  { value: 'APPAREL', label: 'Apparel' },
  { value: 'DRINKWARE', label: 'Drinkware' },
  { value: 'ACCESSORIES', label: 'Accessories' },
  { value: 'HOME_DECOR', label: 'Home Decor' },
  { value: 'STATIONERY', label: 'Stationery' },
  { value: 'CUSTOM', label: 'Custom' },
];

const STATUS_BADGE: Record<string, 'success' | 'warning' | 'default'> = {
  ACTIVE: 'success',
  DRAFT: 'warning',
  ARCHIVED: 'default',
};

interface ConfirmDialog {
  id: string;
  name: string;
  action: 'archive' | 'delete';
}

export function CatalogListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [type, setType] = useState<TypeFilter>('ALL');
  const [categoryId, setCategoryId] = useState('ALL');
  const [designContext, setDesignContext] = useState<DesignContextFilter>('ALL');
  const [dialog, setDialog] = useState<ConfirmDialog | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 300);

  const { data: categoriesData } = useQuery<{ data: Category[] }>({
    queryKey: ['catalog-categories'],
    queryFn: () => api.get<{ data: Category[] }>('/api/v1/admin/catalog/categories'),
  });

  const categoryOptions = useMemo(() => {
    const cats = categoriesData?.data ?? [];
    return [
      { value: 'ALL', label: 'All Categories' },
      ...cats.map((c) => ({ value: c.id, label: c.name })),
    ];
  }, [categoriesData]);

  const { data, isLoading } = useQuery<TemplatesResponse>({
    queryKey: ['catalog-templates', { page, search: debouncedSearch, status, type, categoryId, designContext }],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit: 20 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (status !== 'ALL') params.status = status;
      if (type !== 'ALL') params.type = type;
      if (categoryId !== 'ALL') params.categoryId = categoryId;
      if (designContext !== 'ALL') params.designContext = designContext;
      const res = await api.get<TemplatesResponse>('/api/v1/admin/catalog/templates', { query: params });
      return res;
    },
  });

  const closeDialog = () => { setDialog(null); setDialogError(null); };

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/admin/catalog/templates/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-templates'] });
      closeDialog();
    },
    onError: (err: Error) => {
      setDialogError(err.message || 'Failed to archive template.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/admin/catalog/templates/${id}/permanent`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-templates'] });
      closeDialog();
    },
    onError: (err: Error) => {
      setDialogError(err.message || 'Failed to delete template.');
    },
  });

  const handleConfirm = () => {
    if (!dialog) return;
    setDialogError(null);
    if (dialog.action === 'archive') {
      archiveMutation.mutate(dialog.id);
    } else {
      deleteMutation.mutate(dialog.id);
    }
  };

  const isPending = archiveMutation.isPending || deleteMutation.isPending;
  const templates = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Product Catalog"
          description="Manage Unified Product Templates, options, variants, and pricing."
          action={
            <Button size="sm" onClick={() => navigate('/catalog/new')}>
              Create Template
            </Button>
          }
        />

        {/* Filters */}
        <div className="mt-4 grid grid-cols-1 gap-3 px-6 md:grid-cols-[1fr_auto_auto_auto_auto]">
          <Input
            placeholder="Search templates..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          <Select
            value={status}
            onChange={(e) => { setStatus(e.target.value as StatusFilter); setPage(1); }}
            options={STATUS_OPTIONS}
          />
          <Select
            value={designContext}
            onChange={(e) => { setDesignContext(e.target.value as DesignContextFilter); setPage(1); }}
            options={CONTEXT_OPTIONS}
          />
          <Select
            value={type}
            onChange={(e) => { setType(e.target.value as TypeFilter); setPage(1); }}
            options={TYPE_OPTIONS}
          />
          <Select
            value={categoryId}
            onChange={(e) => { setCategoryId(e.target.value); setPage(1); }}
            options={categoryOptions}
          />
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : templates.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-sm text-text-tertiary">
            No templates found.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-secondary">
                  <th className="w-10 px-6 py-3 font-medium" />
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Brand / Model</th>
                  <th className="px-6 py-3 font-medium">Type</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Category</th>
                  <th className="px-6 py-3 font-medium">Options</th>
                  <th className="px-6 py-3 font-medium">Variants</th>
                  <th className="px-6 py-3 font-medium">Created</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-surface-secondary/50">
                    <td className="px-6 py-3">
                      {t.thumbnailUrl ? (
                        <img
                          src={t.thumbnailUrl}
                          alt={t.name}
                          // `max-w-none` overrides Tailwind preflight's
                          // `img { max-width: 100% }`, which otherwise clamps the
                          // thumbnail to 0 when this auto-layout table column
                          // collapses to 0 width (the symptom: rendered 0×40).
                          // `shrink-0` keeps the fixed 40px box if a flex ancestor
                          // is ever introduced.
                          className="h-10 w-10 max-w-none shrink-0 rounded-md object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-surface-secondary text-text-tertiary">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                            <path fillRule="evenodd" d="M1 5.25A2.25 2.25 0 013.25 3h13.5A2.25 2.25 0 0119 5.25v9.5A2.25 2.25 0 0116.75 17H3.25A2.25 2.25 0 011 14.75v-9.5zm1.5 5.81v3.69c0 .414.336.75.75.75h13.5a.75.75 0 00.75-.75v-2.69l-2.22-2.219a.75.75 0 00-1.06 0l-1.91 1.909-4.97-4.969a.75.75 0 00-1.06 0L2.5 11.06zM12 7a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <Link to={`/catalog/${t.id}`} className="font-medium text-brand-600 hover:underline">
                        {t.name}
                      </Link>
                    </td>
                    <td className="px-6 py-3">
                      {t.brand || t.modelName ? (
                        <span className="text-sm text-text-primary">
                          {[t.brand, t.modelName].filter(Boolean).join(' ')}
                        </span>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant="default" size="sm">{t.productType.replace(/_/g, ' ')}</Badge>
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant={STATUS_BADGE[t.status] ?? 'default'} size="sm">{t.status}</Badge>
                    </td>
                    <td className="px-6 py-3 text-text-secondary">{t.categoryName ?? '—'}</td>
                    <td className="px-6 py-3">
                      {t.optionSummary ? (
                        <span className="text-sm text-text-secondary">{t.optionSummary}</span>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3">
                      {t.variantCount > 0 ? (
                        <Badge variant="default" size="sm">{t.variantCount}</Badge>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-text-secondary">
                      {new Date(t.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => navigate(`/catalog/${t.id}`)}>
                          View
                        </Button>
                        {t.status !== 'ARCHIVED' ? (
                          <button
                            type="button"
                            onClick={() => setDialog({ id: t.id, name: t.name, action: 'archive' })}
                            className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100"
                          >
                            Archive
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setDialog({ id: t.id, name: t.name, action: 'delete' })}
                            className="inline-flex items-center rounded-md border border-danger-300 bg-danger-50 px-2.5 py-1 text-xs font-medium text-danger-700 transition-colors hover:bg-danger-100"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && (
          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            limit={pagination.limit}
            onPageChange={setPage}
          />
        )}
      </Card>

      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/40" onClick={() => !isPending && closeDialog()} />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-surface p-6 shadow-xl">
            <div className="mb-1 flex items-center gap-3">
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                dialog.action === 'delete' ? 'bg-danger-100' : 'bg-amber-100'
              }`}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`h-5 w-5 ${
                  dialog.action === 'delete' ? 'text-danger-600' : 'text-amber-600'
                }`}>
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-base font-semibold text-text-primary">
                {dialog.action === 'delete' ? 'Delete Template' : 'Archive Template'}
              </h3>
            </div>

            <p className="mt-3 text-sm text-text-secondary">
              {dialog.action === 'delete'
                ? <>Are you sure you want to permanently delete <strong className="text-text-primary">{dialog.name}</strong>? This action cannot be undone and all related data will be removed.</>
                : <>Are you sure you want to archive <strong className="text-text-primary">{dialog.name}</strong>? It will no longer appear in active listings.</>
              }
            </p>

            {dialogError && (
              <div className="mt-3 rounded-md bg-danger-50 p-3 text-sm text-danger-700">
                {dialogError}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-3">
              <Button size="sm" variant="outline" disabled={isPending} onClick={closeDialog}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant={dialog.action === 'delete' ? 'danger' : 'primary'}
                disabled={isPending}
                onClick={handleConfirm}
              >
                {isPending
                  ? (dialog.action === 'delete' ? 'Deleting...' : 'Archiving...')
                  : (dialog.action === 'delete' ? 'Yes, Delete' : 'Yes, Archive')
                }
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
